import React, { useState } from 'react';
import { logRideEvent } from '../rideTimeline';
import './RidePage.css';

// Detail screen for one delivery address of a ride.
// Form values (receiver, remark, checked pakbonnen) are local component state
// and disappear when the screen is left, like in the original ToolJet app.
const RideAddress = ({ address, conneoId, rideId, onBack, onProcess }) => {
  const deliveryItems = address.deliveryItems || [];

  const [receiver, setReceiver] = useState(address.signedby || '');
  const [remark, setRemark] = useState(address.comment || '');
  // objectid -> checked; every pakbon starts checked.
  const [checked, setChecked] = useState(() =>
    deliveryItems.reduce((acc, item) => ({ ...acc, [item.objectid]: true }), {})
  );
  const [submitting, setSubmitting] = useState(null); // 'delivered' | 'undeliverable' | null
  const [error, setError] = useState('');
  // The pakbon list can be long and is rarely touched, so it starts collapsed.
  const [showPakbonnen, setShowPakbonnen] = useState(false);

  const checkedCount = deliveryItems.filter(item => checked[item.objectid]).length;
  const allChecked = checkedCount === deliveryItems.length;

  const toggleItem = (objectid) => {
    setChecked(prev => ({ ...prev, [objectid]: !prev[objectid] }));
  };

  const setAll = (value) => {
    setChecked(deliveryItems.reduce((acc, item) => ({ ...acc, [item.objectid]: value }), {}));
    logRideEvent(conneoId, rideId, {
      eventName: value ? 'AllPakbonnenSelected' : 'AllPakbonnenDeselected',
      eventMessage: `${value ? 'Alle' : 'Geen'} pakbonnen geselecteerd voor ${address.afleverNaam}`,
      functionName: 'RideAddress.setAll',
      extraData: { addressKey: address.key, addressLabel: address.afleverNaam, count: deliveryItems.length }
    });
  };

  const togglePakbonnen = () => {
    const next = !showPakbonnen;
    setShowPakbonnen(next);
    logRideEvent(conneoId, rideId, {
      eventName: next ? 'PakbonnenExpanded' : 'PakbonnenCollapsed',
      eventMessage: `Pakbonnenlijst ${next ? 'geopend' : 'gesloten'} voor ${address.afleverNaam}`,
      functionName: 'RideAddress.togglePakbonnen',
      extraData: { addressKey: address.key, addressLabel: address.afleverNaam }
    });
  };

  // Collect the objectids of all checked pakbonnen (items[] of each deliveryItem).
  const selectedDeliveries = () =>
    deliveryItems
      .filter(item => checked[item.objectid])
      .flatMap(item => (item.items && item.items.length ? item.items : [item.objectid]));

  const submit = async (delivered) => {
    if (submitting) return;
    setError('');

    if (delivered && receiver.trim() === '') {
      setError('Naam ontvanger ontbreekt');
      logRideEvent(conneoId, rideId, {
        eventName: 'AddressSubmitBlocked',
        eventType: 'Warning',
        eventMessage: `Afgeleverd geblokkeerd: naam ontvanger ontbreekt (${address.afleverNaam})`,
        functionName: 'RideAddress.submit',
        extraData: { addressKey: address.key, addressLabel: address.afleverNaam }
      });
      return;
    }

    setSubmitting(delivered ? 'delivered' : 'undeliverable');
    try {
      await onProcess(address.key, {
        delivered,
        comment: remark,
        signedby: receiver,
        deliveries: selectedDeliveries()
      });
      // On success the parent switches back to the list; this component unmounts.
    } catch (err) {
      setError(err.message || 'Fout bij verzenden van data');
      setSubmitting(null);
    }
  };

  const statusLabel = address.processed
    ? (address.delivered ? 'Reeds gemeld als afgeleverd' : 'Reeds gemeld als onbestelbaar')
    : null;

  return (
    <div className="ride-detail">
      <div className="ride-detail-address">
        <strong>{address.afleverNaam}</strong>
        {address.afleverPersoon && <div>t.a.v. {address.afleverPersoon}</div>}
        <div>{address.afleverAdres} {address.afleverHuisNr}</div>
        <div>{address.afleverPostcode}</div>
        <div>{address.afleverPlaats}</div>
        <div><strong>{address.pallets} pallets, {address.verpakking} colli</strong></div>
        {address.levertijd && address.levertijd.trim() !== '' && (
          <div className="ride-item-time">Bezorgen voor {address.levertijd}</div>
        )}
        {address.opmerking && address.opmerking.trim() !== '' && (
          <div className="ride-detail-remark">⚠ {address.opmerking}</div>
        )}
        {statusLabel && (
          <div className="ride-detail-status" style={{ color: address.color || '#333' }}>
            {statusLabel}
          </div>
        )}
      </div>

      <div className="ride-form">
        <input
          type="text"
          className="ride-input"
          placeholder="Naam ontvanger"
          value={receiver}
          onChange={e => setReceiver(e.target.value)}
          disabled={!!submitting}
        />
        <textarea
          className="ride-textarea"
          placeholder="Opmerking"
          value={remark}
          onChange={e => setRemark(e.target.value)}
          disabled={!!submitting}
        />

        <div className="ride-pakbonnen">
          <button
            type="button"
            className="ride-pakbonnen-toggle"
            onClick={togglePakbonnen}
            aria-expanded={showPakbonnen}
            disabled={deliveryItems.length === 0}
          >
            <span>
              <strong>Pakbonnen: {deliveryItems.length}</strong>
              <span className={`ride-pakbonnen-status${allChecked ? '' : ' ride-pakbonnen-status-partial'}`}>
                {deliveryItems.length === 0
                  ? 'geen'
                  : allChecked
                    ? 'alle geselecteerd'
                    : `${checkedCount} van ${deliveryItems.length} geselecteerd`}
              </span>
            </span>
            {deliveryItems.length > 0 && (
              <span className="ride-pakbonnen-chevron">{showPakbonnen ? '▲' : '▼'}</span>
            )}
          </button>

          {showPakbonnen && (
            <div className="ride-pakbonnen-list">
              <div className="ride-pakbonnen-actions">
                <button type="button" className="ride-link" onClick={() => setAll(true)} disabled={!!submitting || allChecked}>
                  Alles selecteren
                </button>
                <button type="button" className="ride-link" onClick={() => setAll(false)} disabled={!!submitting || checkedCount === 0}>
                  Alles deselecteren
                </button>
              </div>
              {deliveryItems.map(item => (
                <label key={item.objectid} className="ride-pakbon">
                  <input
                    type="checkbox"
                    checked={!!checked[item.objectid]}
                    onChange={() => toggleItem(item.objectid)}
                    disabled={!!submitting}
                  />
                  <span className="ride-pakbon-label">{item.pakbon}</span>
                  <span className="ride-pakbon-meta">
                    {item.pallets ? `${item.pallets} pallet${item.pallets === 1 ? '' : 's'}, ` : ''}
                    {item.verpakking} colli
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        {error && <p className="error-message">{error}</p>}
      </div>

      <div className="ride-actions">
        <button
          type="button"
          className="ride-btn ride-btn-back"
          onClick={onBack}
          disabled={!!submitting}
        >
          &lt;&lt;
        </button>
        <button
          type="button"
          className="ride-btn ride-btn-undeliverable"
          onClick={() => submit(false)}
          disabled={!!submitting}
        >
          {submitting === 'undeliverable' ? 'Bezig...' : 'Onbestelbaar'}
        </button>
        <button
          type="button"
          className="ride-btn ride-btn-delivered"
          onClick={() => submit(true)}
          disabled={!!submitting}
        >
          {submitting === 'delivered' ? 'Bezig...' : 'Afgeleverd'}
        </button>
      </div>
    </div>
  );
};

export default RideAddress;
